import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Theme = 'light' | 'dark' | 'system';
export type Density = 'comfortable' | 'compact' | 'touch';

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  density: Density;
  setTheme: (t: Theme) => void;
  setDensity: (d: Density) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'zagru.theme';
const DENSITY_KEY = 'zagru.density';

function readStorage(key: string): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  defaultDensity?: Density;
  /** Element to receive data-theme / data-density (defaults to document.documentElement). */
  target?: HTMLElement | null;
}

/** Applies `data-theme` and `data-density` to the document root and exposes setters. */
export function ThemeProvider({ children, defaultTheme = 'system', defaultDensity = 'comfortable', target }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => (readStorage(STORAGE_KEY) as Theme | null) ?? defaultTheme);
  const [density, setDensityState] = useState<Density>(() => (readStorage(DENSITY_KEY) as Density | null) ?? defaultDensity);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(mql.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);

  const resolvedTheme: 'light' | 'dark' = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

  useEffect(() => {
    const el = target ?? (typeof document !== 'undefined' ? document.documentElement : null);
    if (!el) return;
    if (theme === 'system') el.removeAttribute('data-theme');
    else el.setAttribute('data-theme', theme);
    el.setAttribute('data-density', density);
  }, [theme, density, target]);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    writeStorage(STORAGE_KEY, t);
  }, []);
  const setDensity = useCallback((d: Density) => {
    setDensityState(d);
    writeStorage(DENSITY_KEY, d);
  }, []);

  const value = useMemo(() => ({ theme, resolvedTheme, density, setTheme, setDensity }), [theme, resolvedTheme, density, setTheme, setDensity]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      theme: 'system',
      resolvedTheme: systemPrefersDark() ? 'dark' : 'light',
      density: 'comfortable',
      setTheme: () => {},
      setDensity: () => {},
    };
  }
  return ctx;
}
